import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useAuth } from './useAuth'
import type { SocialPlatform } from '@/types'

// Types
export interface PlanEntitlement {
  id: number;
  plan_id: number;
  platform_id: number;
  is_enabled: boolean;
  max_posts_per_day?: number;
  max_posts_per_month?: number;
  created_at: string;
  updated_at: string;
}

export interface PlanEntitlementWithPlatform extends PlanEntitlement {
  platform: SocialPlatform;
}

export interface PlanEntitlementCreate {
  plan_id: number;
  platform_id: number;
  is_enabled?: boolean;
  max_posts_per_day?: number;
  max_posts_per_month?: number;
}

export interface PlanEntitlementUpdate {
  is_enabled?: boolean;
  max_posts_per_day?: number;
  max_posts_per_month?: number;
}

// Query keys
export const planEntitlementQueryKeys = {
  all: ['plan-entitlements'] as const,
  lists: () => [...planEntitlementQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...planEntitlementQueryKeys.lists(), { filters }] as const,
  byPlan: (planId: number) => [...planEntitlementQueryKeys.all, 'plan', planId] as const,
  user: () => [...planEntitlementQueryKeys.all, 'user', 'current'] as const,
  userAvailable: () => ['platforms', 'user', 'available'] as const,
} as const

// Hooks
export const usePlanEntitlements = (filters?: {
  plan_id?: number;
  platform_id?: number;
  is_enabled?: boolean;
}) => {
  return useQuery({
    queryKey: planEntitlementQueryKeys.list(filters || {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.plan_id) params.append('plan_id', filters.plan_id.toString());
      if (filters?.platform_id) params.append('platform_id', filters.platform_id.toString());
      if (filters?.is_enabled !== undefined) params.append('is_enabled', filters.is_enabled.toString());
      
      const response = await api.get(`/plan-entitlements/?${params.toString()}`);
      return response.data as PlanEntitlementWithPlatform[];
    },
  });
};

export const usePlanEntitlementsByPlan = (planId: number | null) => {
  return useQuery({
    queryKey: planEntitlementQueryKeys.byPlan(planId || 0),
    queryFn: async () => {
      if (!planId) return [];
      
      const response = await api.get(`/plan-entitlements/plan/${planId}`);
      return response.data as PlanEntitlementWithPlatform[];
    },
    enabled: !!planId,
  });
};

export const useUserPlanEntitlements = () => {
  return useQuery({
    queryKey: planEntitlementQueryKeys.user(),
    queryFn: async () => {
      const response = await api.get('/plan-entitlements/user/current');
      return response.data as PlanEntitlementWithPlatform[];
    },
  });
};

export const useUserAvailablePlatforms = () => {
  // Check if user is authenticated before making the API call
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: planEntitlementQueryKeys.userAvailable(),
    queryFn: async () => {
      try {
        // console.log('🔄 Fetching user available platforms...');
        const response = await api.get('/lookups/platforms');
        
        // console.log('✅ User available platforms response:', response.data);
        return response.data as SocialPlatform[];
      } catch (error) {
        // console.error('❌ Error fetching user available platforms:', error);
        throw error;
      }
    },
    enabled: isAuthenticated, // Only run when authenticated
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreatePlanEntitlement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: PlanEntitlementCreate) => {
      const response = await api.post('/plan-entitlements/', data);
      return response.data as PlanEntitlement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planEntitlementQueryKeys.all });
    },
  });
};

export const useUpdatePlanEntitlement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PlanEntitlementUpdate }) => {
      const response = await api.put(`/plan-entitlements/${id}`, data);
      return response.data as PlanEntitlement;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planEntitlementQueryKeys.all });
    },
  });
};

export const useDeletePlanEntitlement = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/plan-entitlements/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planEntitlementQueryKeys.all });
    },
  });
};
